import { pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { NumberService, useLocalizeText } from '@mews/framework';
import { SimpleAmount } from '@mews-commander/types';
import { TextMessageCountry } from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { getCultureCode } from '@mews-commander/core';
import { getCurrencyCultureInfo, getCurrencyLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { getCountryTextKey } from '@mews-commander/legacy-entities/src/ducks/countries';
import { useCommanderSelector } from '@mews-commander/utils';
import { Prices, findCountriesFromSelect } from './findCountries';

interface Country {
    label: string;
    costPerSmsMessage: SimpleAmount;
}

const byCountryName = pipe(
    string.Ord,
    ord.contramap((country: Country) => country.label)
);

interface UseSupportedCountriesProps {
    smsCostPrecision: number;
    supportedCountries: TextMessageCountry[];
}

export const useSupportedCountries = ({ smsCostPrecision, supportedCountries }: UseSupportedCountriesProps) => {
    const localizeText = useLocalizeText();

    const countries = useCommanderSelector((state) =>
        pipe(
            supportedCountries,
            array.map((country) => ({
                label: localizeText({
                    textKey: getCountryTextKey(country.countryCode),
                }),
                ...country,
            })),
            array.sortBy([byCountryName]),
            array.map(({ costPerSmsMessage, countryCode, label }) => {
                const { currencyCode, value } = costPerSmsMessage;

                /**
                 * This code intentionally avoids using `localizeCurrencyValue` and instead
                 * collects the information on its own. By passing the same arguments to both formatting
                 * and unformatting functions, we make sure that the two operations remain synchronized,
                 * without depending on assumptions from `localizeCurrencyValue`'s implementation details.
                 */
                const cultureCode = getCultureCode();
                const currencyLocalizationInfo = getCurrencyLocalizationInfo(state, { currencyCode, cultureCode });
                const currencyCultureInfo = getCurrencyCultureInfo(state, { cultureCode });
                const localizationInfo = { ...currencyLocalizationInfo, ...currencyCultureInfo };

                const formattedPrice = NumberService.formatCurrencyValue(value, localizationInfo, {
                    precision: smsCostPrecision,
                });

                /**
                 * To allow users to search based on the displayed, rounded price, instead of the
                 * precise value, we use the unformatted price instead of costPerSmsMessage.value.
                 */
                const unformattedPrice = NumberService.unformat(formattedPrice, localizationInfo);
                return {
                    label,
                    secondaryText: formattedPrice,
                    price: unformattedPrice,
                    value: countryCode,
                };
            })
        )
    );

    const prices: Prices = pipe(
        countries,
        array.map(({ secondaryText, price }) => [secondaryText, price]),
        Object.fromEntries
    );

    const customSearch = findCountriesFromSelect({ prices });

    const countryOptions = countries.map(createOption);

    return { countryOptions, customSearch };
};

interface UseUnsupportedCountriesProps {
    unsupportedCountryCodes: string[];
}

export const useUnsupportedCountries = ({ unsupportedCountryCodes }: UseUnsupportedCountriesProps) => {
    const localizeText = useLocalizeText();

    const unsupportedCountriesContent = pipe(
        unsupportedCountryCodes,
        array.map((country) => localizeText({ textKey: getCountryTextKey(country) })),
        array.sort(string.Ord)
    );

    return unsupportedCountriesContent;
};
